import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/lib/toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { useVisualViewportInset } from '@/hooks/useVisualViewportInset';
import { useEmailField } from '@/hooks/useEmailField';
import { cn } from '@/lib/utils';

interface PlatformContactSheetProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

/**
 * Public "Contact CardSimp" modal — routes to support@cardsimp.com via
 * the shared `send-contact-email` edge function. No auth required.
 * Distinct from `StorefrontContactSheet`, which routes to an individual
 * seller's account email.
 */
export function PlatformContactSheet({ open, onOpenChange }: PlatformContactSheetProps) {
	const isMobile = useIsMobile();
	const { keyboardHeight } = useVisualViewportInset();
	const [name, setName] = useState('');
	const email = useEmailField('', { requireReplyable: true });
	const [message, setMessage] = useState('');
	const [website, setWebsite] = useState(''); // honeypot
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		if (!open) {
			const t = setTimeout(() => {
				setName('');
				email.reset();
				setMessage('');
				setWebsite('');
				setBusy(false);
			}, 300);
			return () => clearTimeout(t);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const submit = async () => {
		if (!name.trim() || !message.trim()) {
			toast.error('Please fill out every field.');
			return;
		}
		if (!email.validate()) {
			toast.error(email.error ?? 'Please enter a valid email address.');
			return;
		}
		setBusy(true);
		try {
			const { data, error } = await supabase.functions.invoke('send-contact-email', {
				body: {
					name: name.trim(),
					email: email.normalized,
					subject: 'Question / Support',
					message: message.trim(),
					pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
					userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
					website,
				},
			});
			if (error || (data as any)?.error) {
				const msg = (data as any)?.error ?? error?.message ?? 'Please try again.';
				toast.error('Could not send message', { description: msg });
				return;
			}
			toast.forceSuccess('Message sent to CardSimp', {
				description: "We'll reply to your email shortly.",
			});
			onOpenChange(false);
		} catch (err: any) {
			toast.error('Could not send message', {
				description: err?.message ?? 'Please try again.',
			});
		} finally {
			setBusy(false);
		}
	};

	const body = (
		<div className="flex flex-col gap-4 pb-6 sm:pb-0">
			{/* Honeypot */}
			<input
				type="text"
				name="website"
				value={website}
				onChange={(e) => setWebsite(e.target.value)}
				tabIndex={-1}
				autoComplete="off"
				className="hidden"
				aria-hidden="true"
			/>

			<Input
				id="pc-name"
				value={name}
				onChange={(e) => setName(e.target.value)}
				placeholder="Your name"
				maxLength={100}
				autoComplete="name"
				aria-label="Your name"
			/>

			<div>
				<Input
					id="pc-email"
					type="email"
					inputMode="email"
					value={email.value}
					onChange={(e) => email.setValue(e.target.value)}
					onBlur={email.onBlur}
					placeholder="Your email"
					maxLength={255}
					autoComplete="email"
					aria-label="Your email"
					aria-invalid={email.showError || undefined}
					className={cn(email.showError && 'border-destructive focus-visible:ring-destructive')}
				/>
				{email.showError && (
					<p className="mt-1 text-[11px] text-destructive">{email.error}</p>
				)}
			</div>

			<Textarea
				id="pc-message"
				value={message}
				onChange={(e) => setMessage(e.target.value)}
				placeholder="How can the CardSimp team help? Questions, bug reports, partnership ideas — all welcome."
				rows={6}
				maxLength={2000}
				aria-label="Message"
			/>

			<Button
				onClick={submit}
				disabled={busy || !name.trim() || !message.trim() || !email.isValid}
				className="w-full h-11"
			>
				{busy ? (
					<>
						<Loader2 className="mr-2 h-4 w-4 animate-spin" />
						Sending…
					</>
				) : (
					<>Send Message</>
				)}
			</Button>
		</div>
	);

	if (isMobile) {
		return (
			<Sheet open={open} onOpenChange={onOpenChange}>
				<SheetContent
					side="bottom"
					className="max-h-[92dvh] flex flex-col"
					style={{ paddingBottom: `calc(env(safe-area-inset-bottom) + 16px + ${keyboardHeight}px)` }}
				>
					<SheetHeader className="text-left">
						<SheetTitle>Contact CardSimp</SheetTitle>
						<SheetDescription>
							Reach the CardSimp team directly. We reply to your email.
						</SheetDescription>
					</SheetHeader>
					<div className="mt-4 overflow-y-auto">{body}</div>
				</SheetContent>
			</Sheet>
		);
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:w-[560px] sm:max-w-[calc(100vw-2rem)]">
				<DialogHeader>
					<DialogTitle>Contact CardSimp</DialogTitle>
					<DialogDescription>
						Reach the CardSimp team directly. We reply to your email.
					</DialogDescription>
				</DialogHeader>
				{body}
			</DialogContent>
		</Dialog>
	);
}

export default PlatformContactSheet;
